import { TypographyProps } from "@mui/material";
import { useCallback } from "react";
import { useSelectedClient } from "../../Queries/ClientQueries";

export type MessageChannel = "email" | "text";

export interface QuotaMessageResult {
  message: string | null;
  status: TypographyProps["color"];
}

export function useQuotaMessaging() {
  const { data: selectedClient } = useSelectedClient();

  const getQuotaStatus = useCallback(
    (channel: MessageChannel, segmentCount: number, recipientCount: number): QuotaMessageResult => {
      if (!selectedClient) {
        return { message: null, status: "info" };
      }
      const totalToSend = segmentCount * recipientCount;
      const isEmail = channel === "email";

      const currentSent = isEmail ? selectedClient.sentEmailCount : selectedClient.sentTextCount;
      const quotaLimit = isEmail
        ? (selectedClient.quotas?.emailQuota ?? 0)
        : (selectedClient.quotas?.textQuota ?? 0);

      const newTotal = currentSent + totalToSend;
      const remainingAfterSend = Math.max(0, quotaLimit - newTotal);

      const alreadyOverQuota = currentSent >= quotaLimit;
      const overageExpected = newTotal > quotaLimit;

      let message = `This message will use ${totalToSend}, bringing your total to ${newTotal}.`;
      let status: TypographyProps["color"] = "info";

      if (alreadyOverQuota) {
        message = `You have already used ${currentSent} of ${quotaLimit} monthly ${channel} messages. ${message}`;
        status = "error.main";
      } else if (overageExpected) {
        message = `You have used ${currentSent} of ${quotaLimit} monthly ${channel} messages. Sending this message will exceed your quota. It will use ${totalToSend}, bringing your total to ${newTotal}.`;
        status = "warning.main";
      } else {
        message = `You have used ${currentSent} of ${quotaLimit} monthly ${channel} messages. ${message} You will have ${remainingAfterSend} remaining.`;
      }

      return {
        message,
        status,
      };
    },
    [selectedClient],
  );

  return {
    getQuotaStatus,
  };
}
